import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import "../../i18/config";
import { getDataStoreRecords, getDataStores, removeDataStore, removeDataStoreRecord, saveDataStore, saveDataStoreRecord } from "../../api";
import BreadCrumb from "../../components/Common/BreadCrumb";
import Loader from "../../components/Loader";
import LeftbarTitle from "../../components/LeftbarTitle";
import RemoveModal from "../../components/RemoveForm";
import { DataStore, DataStoreRecord } from "./types";
import ListCard from "./ui/ListCard";
import WorkflowTable from "./ui/WorkflowTable";
import RowActions, { actionsColumn } from "./ui/RowActions";
import FormPanel from "./ui/FormPanel";

const PER_PAGE = 50;

/** Automation → Data stores: keyed records that scenarios keep between runs. */
const DataStoreList = () => {
    const { t } = useTranslation(['workflow', 'menu', 'common']);
    const [stores, setStores] = useState<DataStore[] | null>(null);
    const [form, setForm] = useState<{ uuid?: string; name: string; maxRecords: number } | null>(null);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState<DataStore | null>(null);
    const [records, setRecords] = useState<DataStoreRecord[] | null>(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState<{ key: string; json: string; isNew: boolean } | null>(null);
    const [removingStore, setRemovingStore] = useState<DataStore | null>(null);
    const [removingRecord, setRemovingRecord] = useState<DataStoreRecord | null>(null);

    const fail = (e: any) => toast.error(e?.message || t('workflow:actionFailed'));
    const load = useCallback(() => {
        getDataStores().then((r: any) => setStores(r.items || [])).catch(() => setStores([]));
    }, []);
    useEffect(() => load(), [load]);

    const loadRecords = useCallback(() => {
        if (!open) {
            return;
        }
        getDataStoreRecords(open.uuid, { page, maxResults: PER_PAGE })
            .then((r: any) => { setRecords(r.items || []); setTotal(r.meta?.totalCount || 0); })
            .catch(() => setRecords([]));
    }, [open, page]);
    useEffect(() => loadRecords(), [loadRecords]);

    const openStore = (store: DataStore) => { setOpen(store); setPage(1); setRecords(null); };

    const saveStore = () => {
        if (!form || !form.name.trim()) {
            return;
        }
        setSaving(true);
        saveDataStore({ uuid: form.uuid, name: form.name.trim(), maxRecords: form.maxRecords })
            .then(() => { setForm(null); load(); })
            .catch(fail)
            .finally(() => setSaving(false));
    };

    const deleteStore = (store: DataStore) =>
        removeDataStore(store.uuid).then(load).catch(fail).finally(() => setRemovingStore(null));

    const saveRecord = () => {
        if (!open || !editing) {
            return;
        }
        let data: any;
        try {
            data = JSON.parse(editing.json);
        } catch (e) {
            toast.error(t('workflow:invalidJson'));

            return;
        }
        if (typeof data !== 'object' || data === null || Array.isArray(data)) {
            toast.error(t('workflow:recordMustBeObject'));

            return;
        }
        saveDataStoreRecord(open.uuid, { key: editing.key.trim() || undefined, data, overwrite: !editing.isNew })
            .then(() => { setEditing(null); loadRecords(); load(); })
            .catch(fail);
    };

    const deleteRecord = (record: DataStoreRecord) => {
        if (open) {
            removeDataStoreRecord(open.uuid, record.key).then(() => { loadRecords(); load(); }).catch(fail).finally(() => setRemovingRecord(null));
        }
    };

    const storeColumns = [
        actionsColumn((s: DataStore) => (
            <RowActions actions={[
                { icon: 'ri-table-fill', label: t('workflow:records'), onClick: () => openStore(s) },
                { icon: 'ri-pencil-fill', label: t('common:edit'), onClick: () => setForm({ uuid: s.uuid, name: s.name, maxRecords: s.maxRecords }), hidden: !s.actions?.canEdit },
                'divider',
                { icon: 'ri-delete-bin-fill', label: t('common:delete'), onClick: () => setRemovingStore(s), hidden: !s.actions?.canRemove, danger: true },
            ]} />
        )),
        {
            name: t('workflow:name'),
            grow: 3,
            cell: (s: DataStore) => <button type="button" className="btn btn-link p-0 fw-medium text-body fs-14 text-decoration-none" onClick={() => openStore(s)}>{s.name}</button>,
        },
        {
            name: t('workflow:records'),
            grow: 2,
            cell: (s: DataStore) => {
                const percent = Math.min(100, Math.round((s.records / Math.max(1, s.maxRecords)) * 100));

                return (
                    <div className="w-100">
                        <div className="mb-1">{s.records.toLocaleString()} / {s.maxRecords.toLocaleString()}</div>
                        <div className="progress progress-sm"><div className={"progress-bar " + (percent >= 90 ? 'bg-danger' : 'bg-primary')} style={{ width: percent + '%' }} /></div>
                    </div>
                );
            },
        },
    ];

    const recordColumns = [
        actionsColumn((r: DataStoreRecord) => (
            <RowActions actions={[
                { icon: 'ri-pencil-fill', label: t('common:edit'), onClick: () => setEditing({ key: r.key, json: JSON.stringify(r.data, null, 2), isNew: false }), hidden: !open?.actions?.canEdit },
                { icon: 'ri-delete-bin-fill', label: t('common:delete'), onClick: () => setRemovingRecord(r), hidden: !open?.actions?.canEdit, danger: true },
            ]} />
        )),
        { name: t('workflow:key'), grow: 2, cell: (r: DataStoreRecord) => <span className="font-monospace">{r.key}</span> },
        { name: t('workflow:record'), grow: 5, cell: (r: DataStoreRecord) => <span className="font-monospace text-truncate d-block" style={{ maxWidth: 560 }}>{JSON.stringify(r.data)}</span> },
    ];

    return (
        <>
            <BreadCrumb title={t('menu:automationDataStores')} pageTitle={t('menu:automation')} />
            <Row className="g-4 mb-3">
                <div className="col-sm-auto">
                    <button type="button" className="btn btn-success" onClick={() => setForm({ name: '', maxRecords: 1000 })}><i className="ri-add-line align-bottom me-1" />{t('workflow:newDataStore')}</button>
                </div>
            </Row>

            <ListCard title={t('menu:automationDataStores')} filters={<p className="text-muted mb-0">{t('workflow:dataStoresIntro')}</p>}>
                {stores === null ? <Loader /> :
                    stores.length === 0
                        ? <div className="text-center text-muted py-5"><i className="ri-database-2-line display-5 d-block mb-2" /><p className="fs-14 mb-0">{t('workflow:noDataStores')}</p></div>
                        : <WorkflowTable columns={storeColumns as any} data={stores} />}
            </ListCard>

            <FormPanel
                show={form !== null}
                title={form?.uuid ? t('workflow:editDataStore') : t('workflow:newDataStore')}
                saving={saving}
                canSave={!!form?.name.trim()}
                onSave={saveStore}
                onClose={() => setForm(null)}
            >
                {form && <>
                    <div className="mb-3">
                        <label className="form-label" htmlFor="wf-store-name">{t('workflow:name')}</label>
                        <input id="wf-store-name" className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                        <label className="form-label" htmlFor="wf-store-max">{t('workflow:maxRecords')}</label>
                        <input id="wf-store-max" type="number" min={1} max={100000} className="form-control" value={form.maxRecords} onChange={(e) => setForm({ ...form, maxRecords: Math.max(1, Math.min(100000, parseInt(e.target.value, 10) || 1)) })} />
                    </div>
                </>}
            </FormPanel>

            <Modal show={open !== null} onHide={() => setOpen(null)} size="xl" scrollable>
                <Modal.Header closeButton><LeftbarTitle title={open?.name || ''} /></Modal.Header>
                <Modal.Body>
                    {open?.actions?.canEdit &&
                        <button type="button" className="btn btn-success btn-sm mb-3" onClick={() => setEditing({ key: '', json: '{\n  \n}', isNew: true })}>
                            <i className="ri-add-line align-bottom me-1" />{t('workflow:newRecord')}
                        </button>}
                    {records === null ? <Loader /> :
                        records.length === 0
                            ? <div className="text-muted fs-14 py-3">{t('workflow:noRecords')}</div>
                            : <WorkflowTable columns={recordColumns as any} data={records} paging={{ page, perPage: PER_PAGE, total, onPage: setPage }} />}
                </Modal.Body>
            </Modal>

            <Modal show={editing !== null} onHide={() => setEditing(null)}>
                <Modal.Header closeButton><LeftbarTitle title={editing?.isNew ? t('workflow:newRecord') : (editing?.key || '')} /></Modal.Header>
                {editing && <Modal.Body>
                    {editing.isNew && <div className="mb-3">
                        <label className="form-label" htmlFor="wf-record-key">{t('workflow:key')}</label>
                        <input id="wf-record-key" className="form-control font-monospace" value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value })} />
                        <div className="form-text">{t('workflow:keyHint')}</div>
                    </div>}
                    <label className="form-label" htmlFor="wf-record-json">{t('workflow:record')} (JSON)</label>
                    <textarea id="wf-record-json" className="form-control font-monospace" rows={10} value={editing.json} onChange={(e) => setEditing({ ...editing, json: e.target.value })} />
                </Modal.Body>}
                <Modal.Footer>
                    <Button variant="light" onClick={() => setEditing(null)}>{t('common:cancel')}</Button>
                    <Button variant="primary" onClick={saveRecord}>{t('common:save')}</Button>
                </Modal.Footer>
            </Modal>

            <RemoveModal
                isOpen={removingStore !== null}
                removeItem={removingStore}
                modalTitle={t('common:delete')}
                onRemove={(s: DataStore) => deleteStore(s)}
                handleClose={() => setRemovingStore(null)}
            />
            <RemoveModal
                isOpen={removingRecord !== null}
                removeItem={removingRecord ? { name: removingRecord.key } : null}
                modalTitle={t('common:delete')}
                onRemove={() => removingRecord && deleteRecord(removingRecord)}
                handleClose={() => setRemovingRecord(null)}
            />
        </>
    );
};

export default DataStoreList;
